import type { ReactElement } from "react"
import { useContext } from "react"

import { useTranslation } from "react-i18next"

import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import { Grid } from "@mui/material"
import { Button, Typography } from "@synapse-analytics/synapse-ui"
import moment from "moment"

import { InfoBlock } from "../../../../../components/containers/InfoBlock"
import { Context } from "../../../Context"
import { ConfigurationSlider } from "../ConfigurationSlider"

import styles from "./ClassificationHistoryView.module.scss"

/**
 * ClassificationHistoryView component
 *
 * This component is used to display the configuration history of a model
 * It displays the configuration meta data such as the date started, date ended, target and target type
 * It also displays the configuration labels and the uncovered range
 *
 * @returns {ReactElement} - The component element.
 */
export function ClassificationHistoryView(): ReactElement {
  const { currentConfigHistory, setCurrentConfigHistory, isSidebarOpen, setIsSidebarOpen } = useContext(Context)
  const { t } = useTranslation("ModelConfigurations")

  const groupedConfigObj = Object.groupBy(currentConfigHistory?.settings || [], (item) => item.type)

  const sortedConfigLabelsArr = groupedConfigObj["label"]?.sort((a, b) => a.lower_bound - b.upper_bound)

  const uncoveredArr: number[][] = []

  if (
    sortedConfigLabelsArr &&
    sortedConfigLabelsArr.length === 1 &&
    sortedConfigLabelsArr[0] &&
    groupedConfigObj["boundary"]?.[0]
  ) {
    uncoveredArr.push([sortedConfigLabelsArr[0].upper_bound + 1, groupedConfigObj["boundary"][0].upper_bound || 100])
  } else if (sortedConfigLabelsArr) {
    const lastLabel = sortedConfigLabelsArr[sortedConfigLabelsArr.length - 1]

    if (sortedConfigLabelsArr[0] && sortedConfigLabelsArr[0].lower_bound > 0) {
      uncoveredArr.push([0, sortedConfigLabelsArr[0].lower_bound - 1])
    }

    for (let i = 0; i < sortedConfigLabelsArr.length - 1; i++) {
      const currentLabel = sortedConfigLabelsArr[i]
      const nextLabel = sortedConfigLabelsArr[i + 1]

      if (nextLabel && currentLabel && nextLabel.lower_bound - currentLabel.upper_bound > 0) {
        uncoveredArr.push([currentLabel.upper_bound + 1, nextLabel.lower_bound - 1])
      }
    }

    if (lastLabel && lastLabel.upper_bound < 100) {
      uncoveredArr.push([lastLabel.upper_bound + 1, 100])
    }
  }

  return (
    <section>
      <section>
        <header className={styles.headerContainer}>
          <Typography variant="h2-bold" gutterBottom>
            {t("historyView.metadata")}
          </Typography>
          <div>
            <Button
              onClick={() => setCurrentConfigHistory(undefined)}
              variant="primary"
              startIcon={<AddCircleOutlineOutlinedIcon fontSize="small" />}
              size="regular"
            >
              {t("historyView.newConfiguration")}
            </Button>
            {!isSidebarOpen && (
              <Button variant="secondary" onClick={setIsSidebarOpen.bind(null, (prev) => !prev)}>
                {t("historyView.history")}
              </Button>
            )}
          </div>
        </header>
        <div className={styles.metaDataContainer}>
          <InfoBlock
            title={t("historyView.dateStarted")}
            text={
              currentConfigHistory?.created_at
                ? moment(currentConfigHistory.created_at).format("DD/MM/YYYY, h:mm A")
                : "N/A"
            }
            textTransform="capitalize"
            type="default"
          />

          <InfoBlock
            title={t("historyView.dateEnded")}
            text={
              currentConfigHistory?.deactivation_date
                ? moment(currentConfigHistory.deactivation_date).format("DD/MM/YYYY, h:mm A")
                : "N/A"
            }
            textTransform="capitalize"
            type="default"
          />

          <InfoBlock
            title={t("historyView.target")}
            text={currentConfigHistory?.target_key_path.at(-1) || ""}
            textToolTip={currentConfigHistory?.target_key_path.join(" -> ")}
            titleTooltip={t("tooltips.target")}
            textTransform="initial"
            type="default"
          />

          <InfoBlock
            title={t("historyView.targetType")}
            text={currentConfigHistory?.type || "N/A"}
            titleTooltip={t("tooltips.targetType")}
            textTransform="initial"
            type="default"
          />

          {currentConfigHistory?.auto_add !== null && (
            <InfoBlock
              title={t("historyView.autoAdd")}
              text={currentConfigHistory?.auto_add ? t("booleanValues.true") : t("booleanValues.false")}
              textTransform="capitalize"
              type="default"
              titleTooltip={t("tooltips.autoAdd")}
            />
          )}
        </div>
      </section>

      {currentConfigHistory?.type !== "label" && (
        <section className={styles.configurationLabelsContainer}>
          <header>
            <Typography variant="h2-bold" gutterBottom>
              {t("historyView.configurationLabels")}
            </Typography>
          </header>
          <div>
            <Grid container>
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={6}>
                  <InfoBlock
                    title={t("historyView.minimum")}
                    text={groupedConfigObj["boundary"]?.[0]?.lower_bound || 0}
                    textTransform="initial"
                    type="default"
                  />
                </Grid>

                <Grid item xs={6}>
                  <InfoBlock
                    title={t("historyView.maximum")}
                    text={groupedConfigObj["boundary"]?.[0]?.upper_bound || 100}
                    textTransform="initial"
                    type="default"
                  />
                </Grid>
              </Grid>

              <Grid container item xs={12} spacing={1} mt={2}>
                <Grid item xs={12}>
                  <Typography variant="h3-bold">{t("historyView.labels")}</Typography>
                </Grid>

                <Grid container item spacing={2} direction="column" mb={1}>
                  {groupedConfigObj["label"]?.map((item) => (
                    <Grid item key={item.uuid}>
                      <ConfigurationSlider
                        label={item.label ?? { uuid: "", name: t("historyView.sliderNoLabel") }}
                        min={groupedConfigObj["boundary"]?.[0]?.lower_bound || 0}
                        max={groupedConfigObj["boundary"]?.[0]?.upper_bound || 100}
                        range={[[item.lower_bound, item.upper_bound]]}
                        uncovered
                      />
                    </Grid>
                  ))}
                </Grid>

                <Grid container item xs={12} mb={1}>
                  {uncoveredArr.length > 0 && (
                    <ConfigurationSlider
                      label={{ name: t("historyView.uncovered"), uuid: "uuid" }}
                      min={groupedConfigObj["boundary"]?.[0]?.lower_bound || 0}
                      max={groupedConfigObj["boundary"]?.[0]?.upper_bound || 100}
                      range={uncoveredArr}
                      uncovered
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </div>
        </section>
      )}
    </section>
  )
}
